import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Mail, Loader2, CheckCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email: email.trim() });
      setSent(true);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-12" data-testid="forgot-password-page">
      <Card className="w-full max-w-md rounded-2xl shadow-lg border-0">
        <CardHeader className="text-center pb-2">
          {sent ? (
            <>
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-7 h-7 text-green-600" />
              </div>
              <CardTitle className="text-xl">Check Your Email</CardTitle>
              <CardDescription className="mt-2">
                If an account exists for <strong>{email}</strong>, we've sent a password reset link.
                Please check your inbox and spam folder.
              </CardDescription>
            </>
          ) : (
            <>
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-blue-100 flex items-center justify-center">
                <Mail className="w-7 h-7 text-blue-600" />
              </div>
              <CardTitle className="text-xl">Forgot Password?</CardTitle>
              <CardDescription className="mt-2">
                Enter your email address and we'll send you a link to reset your password.
              </CardDescription>
            </>
          )}
        </CardHeader>
        <CardContent className="pt-4">
          {sent ? (
            <div className="space-y-4">
              <Button
                variant="outline"
                className="w-full rounded-xl"
                onClick={() => { setSent(false); setEmail(""); }}
                data-testid="try-another-email-btn"
              >
                Try another email
              </Button>
              <Link to="/login" className="block">
                <Button variant="ghost" className="w-full rounded-xl" data-testid="back-to-login-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back to Login
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="rounded-xl"
                  required
                  data-testid="forgot-email-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl bg-[#e0482d] hover:bg-[#c93d25] text-white"
                data-testid="send-reset-link-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                Send Reset Link
              </Button>
              <Link to="/login" className="block">
                <Button variant="ghost" className="w-full rounded-xl text-gray-600" data-testid="back-to-login-link">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back to Login
                </Button>
              </Link>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
